import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, generatePath } from "react-router-dom";
import { Select, ConfigProvider, Dropdown, Checkbox, Button, Popover, Space, Skeleton, Progress } from "antd";
import { useRequest } from "ahooks";
import { k_dateWidth } from "./board"
import type { WeekDate, RenderProjectItem, PopProjectItem } from "./board"
import { k_milestoneStatus } from "../detail/milestone/edit"
import { UserSvg, ArrowBottomSvg, AddSvg } from "@/components/svgs"
import { useParams } from "react-router-dom";
import { MilestoneEditStatusModal } from "@/components/project/milestone"
import {
    getOverviewProjectInfoApi, getProjectDashboardAPI, getPlmProjectAllStatusApi
} from "@/services"
import { PopProjectInfo } from "./projectPopContent";
import "./index.scss"
import clsx from "clsx";
import { documentUtil, toHsl, parseTime, getPlmStatusColor, getPlmUserName } from "@/utils"
import dayjs from "dayjs";

interface IBoardRowProps {
    data: RenderProjectItem,
    weekdates: WeekDate[]
    onStatusChange: (val: number, statusLabel: string, item: API.PROJECT.OverviewMilestone, isPlm: boolean) => void
    onCreate: (projectID: number, date: string) => void
    onPackageClick: (item: API.PROJECT.OverviewMilestone) => void
}
export const statusOptions = [{
    value: k_milestoneStatus.unstart,
    label: <span className=""> 未完成 </span>,
    valueLabel: "未完成"
}, {
    value: k_milestoneStatus.finished,
    label: <span className=""> 已完成 </span>,
    valueLabel: "已完成"
    }
]
const getStatusCls = (statusLabel: string) => {
    if (statusLabel === "未处理") return "unhandled"
    if (statusLabel === "处理中") return "processing"
    if (statusLabel === "待复核") return "toreview"
    if (statusLabel === "已完成") return "finished"
    if (statusLabel === "已暂停") return "paused"
    return "unhandled"
}

const getItemType = (type: string, isOutdate: boolean) => {
    if (type === "pm") return <span className={clsx("text-xs font-bold", isOutdate ? "text-red-800" : "text-dark-bg")}>标准里程碑</span>
    return <span className={clsx("text-xs font-bold", isOutdate ? "text-red-800" : "text-yellow-500")}>里程碑</span> 
}
interface PlmOption{
    value: number,
    label: React.ReactNode,
    valueLabel: string
}
export const BoardRow: React.FC<IBoardRowProps> = (props) => {
    const { data } = props

    const [plmStatusOpts, setPlmStatusOpts] = useState<PlmOption[]>([]);
    const { data: plmAllStatus = [], loading: plmStatusLoading, run: fetchPlmAllStatus } = useRequest(getPlmProjectAllStatusApi, {
        manual: true,
        cacheKey: `plmAllStatus_${data.id}`,
        staleTime: 30000,
        onSuccess: (res) => {
            setPlmStatusOpts(res.map((v) => {
                return {
                    label: <span>{v.statusName}</span>,
                    value: v.statusId,
                    valueLabel: v.statusName
                }
            }))
        }
    })


    const renderItems = useMemo(() => {
        return props.weekdates.map((v) => {
            return {
                ...v,
                milestones: props.data.milestones.filter((m) => {
                    return m.startDate === v.date
                }).map(v => {
                    return {
                        ...v,
                        isOutdate: !!(v.startDate && dayjs(v.startDate).isBefore(dayjs(), 'day') && v.status !== "已完成")
                    }
                })
            }
        })
    }, [props.data, props.weekdates])

    
    const onStatusChange = (status: number, statusLabel: string, milestone: API.PROJECT.OverviewMilestone, item: typeof renderItems[0]) => {
        if (milestone.typeId === "plm") {
            props.onStatusChange(status, statusLabel, milestone,  true)
            return  
        }
        props.onStatusChange(status, statusLabel, milestone, false)
        // saveMilestoneStatus(milestone.id!, {
        //     status: status,
        //     code: milestone.milestoneCode!
        // })
    }
    const onMilestoneItemClick = (item: API.PROJECT.OverviewMilestone) => {
        if (item.typeId === "pm") {
            const path = generatePath(`/projects/${props.data.id}/milestone`)
            window.open(path, "_blank")
            return
        }
        if(!props.data.createMilestone) return
        props.onPackageClick(item)
    }
    const onPlmStatusClick = (item: API.PROJECT.OverviewMilestone) => {
        if (item.typeId === "pm") return
        window.open(`${documentUtil.getPpmProjectWorkPackagePath(item.id, props.data.id)}`, "_blank");
    }

    const { data: projectInfo, loading: infoLoading, run: getOverviewProjectInfo } = useRequest(getOverviewProjectInfoApi, {
        manual: true,
        cacheKey: `overviewProjectInfo_${data.id}`,
        staleTime: 30000
    })
    const { data: projectDashboard, loading: dashboardLoading, run: getProjectDashboard } = useRequest(getProjectDashboardAPI, {
        manual: true,
        cacheKey: `overviewProjectDashboard_${data.id}`,
        staleTime: 30000
    })
    const onPopHover = () => {
        getOverviewProjectInfo(data.id)
        getProjectDashboard(data.id)
    }
    return (
        <div className="relative w-max flex min-w-full">
            <Popover placement="right" onOpenChange={onPopHover} content={<PopProjectInfo loading={infoLoading || dashboardLoading} project={data} info={projectInfo} dashboard={projectDashboard}></PopProjectInfo>}>
                <div className="w-60 bg-gray-50 border border-solid border-white p-2 sticky z-10 left-0">
                    <div className="text-gray-500 text-xs">{data.code}</div>
                    <a className="text-gray-900 font-bold text-sm hover:underline cursor-pointer" rel="noreferrer" href={`/projects/${data.id}/dashboard`} target="_blank">{data.name}</a>
                </div>
            </Popover>

            {renderItems.map((v, i) => (
                <div key={i} className={clsx("border border-solid border-white p-1 flex flex-col gap-1 flex-1 relative overflow-hidden", v.isToday ? "bg-orange-100" : "bg-gray-100")} style={{ minWidth: k_dateWidth }}>
                    {v.milestones.map((m, j) => (
                        <div key={j} className={clsx("bg-white rounded-md p-1 shadow-lg", m.isOutdate ? 'bg-red-100 text-red-800' : 'bg-white', m.typeId === "plm" && "cursor-pointer hover:bg-gray-50")} onClick={() => onMilestoneItemClick(m)}>
                            <div className="flex items-center justify-between">
                                {m.typeId === "pm" ? (
                                    <Select onClick={(e) => { e.stopPropagation(); }} className={clsx("board-row-item", getStatusCls(m.status), m.isOutdate && "outdate")}
                                        styles={{ popup: { root: { width: "fit-content", minWidth: 150 } } }}
                                        options={statusOptions}
                                        value={m.statusCode}
                                        labelRender={(v) => {
                                            if (v.label) return v.label
                                            return <span>{m.status}</span>
                                        }}
                                        onChange={(val: number, opt: any) => onStatusChange(val, opt.valueLabel, m, v)} />
                                ) : props.data.createMilestone ? (
                                        <Select onClick={(e) => { e.stopPropagation(); }}
                                            onOpenChange={(e) => { e && fetchPlmAllStatus(props.data.id) }}
                                            className={clsx("board-row-item", getStatusCls(m.status), m.isOutdate && "outdate")}
                                            styles={{ popup: { root: { width: "fit-content", minWidth: 150 } } }}
                                            options={plmStatusOpts}
                                            value={m.statusCode}
                                            labelRender={(v) => {
                                                if(v.label) return v.label
                                                return <span>{m.status}</span>
                                            }}
                                            onChange={(val: number, opt: any) => onStatusChange(Number(val), opt.valueLabel, m, v)} />
                                    ) : (
                                            <div className="board-row-status" style={{ backgroundColor: getPlmStatusColor(m.status) }}>{m.status}</div>
                                )}
                                <span className="text-xs">{getItemType(m.typeId, m.isOutdate)}</span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                                <div className="font-bold text-xs w-0 flex-1">{m.name}</div>
                                {m.opUser && (
                                    <div className="w-[30px] h-[30px] leading-[30px] text-[12px] rounded-full text-white whitespace-nowrap scale-75 text-center overflow-hidden"
                                        style={{ background: toHsl(getPlmUserName(m.opUser)) }}>{getPlmUserName(m.opUser)}</div>
                                )}
                            </div>
                        </div>
                    ))}
                    {props.data.createMilestone && (
                        <div className="group flex-1 flex min-h-[60px]">
                            <div onClick={() => props.onCreate(props.data.id, v.date)} className="hidden group-hover:flex group-hover:bg-gray-200 cursor-pointer justify-center items-center flex-1 text-[30px] text-gray-500">
                                <AddSvg></AddSvg>
                            </div>
                        </div>
                    )}
                    
                </div>
            ))}
        </div>
    )
}